import React, { useEffect } from 'react'
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Grid,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Button,
    Alert,
    AlertTitle,
    Breadcrumbs,
    Link,
    Paper
} from '@mui/material'
import {
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon,
    Security as SecurityIcon,
    Home as HomeIcon,
    People as PeopleIcon,
    AttachMoney as MoneyIcon,
    Lightbulb as LightbulbIcon,
    Warning as WarningIcon,
    CheckCircle as CheckIcon,
    Info as InfoIcon,
    ArrowBack as ArrowBackIcon,
    Home as HomeIconBreadcrumb,
    Download as DownloadIcon,
    NavigateNext as NavigateNextIcon,
    Article as ArticleIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const Tips: React.FC = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    interface TipItem {
        title: string
        description: string
        icon: React.ReactNode
    }
    // SEO Meta Tags
    useEffect(() => {
        document.title = 'WG-Tipps & Ratgeber - Professionelle Hilfe für Ihre WG-Suche | WG-Anzeigen'

        // Meta Description
        const metaDescription = document.querySelector('meta[name="description"]')
        if (metaDescription) {
            metaDescription.setAttribute('content', 'Professionelle WG-Tipps für Ihre WG-Suche: Sicherheit, Kosten, WG-Leben & mehr. Umfassender Ratgeber mit Checklisten und FAQ für erfolgreiche WG-Suche.')
        } else {
            const meta = document.createElement('meta')
            meta.name = 'description'
            meta.content = 'Professionelle WG-Tipps für Ihre WG-Suche: Sicherheit, Kosten, WG-Leben & mehr. Umfassender Ratgeber mit Checklisten und FAQ für erfolgreiche WG-Suche.'
            document.head.appendChild(meta)
        }

        // Meta Keywords
        const metaKeywords = document.querySelector('meta[name="keywords"]')
        if (metaKeywords) {
            metaKeywords.setAttribute('content', 'WG-Tipps, WG-Suche, Wohngemeinschaft, WG-Ratgeber, WG-Leben, WG-Kosten, WG-Sicherheit, WG-Checkliste, WG-FAQ')
        } else {
            const meta = document.createElement('meta')
            meta.name = 'keywords'
            meta.content = 'WG-Tipps, WG-Suche, Wohngemeinschaft, WG-Ratgeber, WG-Leben, WG-Kosten, WG-Sicherheit, WG-Checkliste, WG-FAQ'
            document.head.appendChild(meta)
        }

        // Open Graph Tags
        const ogTitle = document.querySelector('meta[property="og:title"]')
        if (ogTitle) {
            ogTitle.setAttribute('content', 'WG-Tipps & Ratgeber - Professionelle Hilfe für Ihre WG-Suche')
        } else {
            const meta = document.createElement('meta')
            meta.setAttribute('property', 'og:title')
            meta.content = 'WG-Tipps & Ratgeber - Professionelle Hilfe für Ihre WG-Suche'
            document.head.appendChild(meta)
        }

        const ogDescription = document.querySelector('meta[property="og:description"]')
        if (ogDescription) {
            ogDescription.setAttribute('content', 'Professionelle WG-Tipps für Ihre WG-Suche: Sicherheit, Kosten, WG-Leben & mehr. Umfassender Ratgeber mit Checklisten und FAQ.')
        } else {
            const meta = document.createElement('meta')
            meta.setAttribute('property', 'og:description')
            meta.content = 'Professionelle WG-Tipps für Ihre WG-Suche: Sicherheit, Kosten, WG-Leben & mehr. Umfassender Ratgeber mit Checklisten und FAQ.'
            document.head.appendChild(meta)
        }

        // Structured Data (JSON-LD)
        const structuredData = {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "WG-Tipps & Ratgeber - Professionelle Hilfe für Ihre WG-Suche",
            "description": "Professionelle WG-Tipps für Ihre WG-Suche: Sicherheit, Kosten, WG-Leben & mehr. Umfassender Ratgeber mit Checklisten und FAQ für erfolgreiche WG-Suche.",
            "author": {
                "@type": "Organization",
                "name": "WG-Anzeigen"
            },
            "publisher": {
                "@type": "Organization",
                "name": "WG-Anzeigen",
                "url": "https://wg-anzeigen.de"
            },
            "datePublished": new Date().toISOString(),
            "dateModified": new Date().toISOString(),
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://wg-anzeigen.de/tips"
            },
            "articleSection": ["WG-Suche", "Sicherheit", "WG-Leben", "Kosten"],
            "keywords": "WG-Tipps, WG-Suche, Wohngemeinschaft, WG-Ratgeber, WG-Leben, WG-Kosten, WG-Sicherheit"
        }

        // Remove existing structured data
        const existingScript = document.querySelector('script[type="application/ld+json"]')
        if (existingScript) {
            existingScript.remove()
        }

        // Add new structured data
        const script = document.createElement('script')
        script.type = 'application/ld+json'
        script.textContent = JSON.stringify(structuredData)
        document.head.appendChild(script)

        return () => {
            // Cleanup on unmount
            document.title = 'WG-Anzeigen'
            const scriptToRemove = document.querySelector('script[type="application/ld+json"]')
            if (scriptToRemove) {
                scriptToRemove.remove()
            }
        }
    }, [])

    interface FaqItem {
        question: string
        answer: string
    }

    const withIcons = (
        items: Array<Pick<TipItem, 'title' | 'description'>>,
        icons: React.ReactNode[]
    ): TipItem[] =>
        items.map((item, index) => ({
            ...item,
            icon: icons[index] ?? icons[icons.length - 1] ?? null
        }))

    const searchTips = withIcons(
        t('tips.sections.search.items', {
            returnObjects: true
        }) as Array<Pick<TipItem, 'title' | 'description'>>,
        [<SearchIcon />, <CheckIcon />, <LightbulbIcon />, <CheckIcon />]
    )

    const securityTips = withIcons(
        t('tips.sections.security.items', {
            returnObjects: true
        }) as Array<Pick<TipItem, 'title' | 'description'>>,
        [<SecurityIcon />, <CheckIcon />, <WarningIcon />, <SecurityIcon />]
    )

    const wgLifeTips = withIcons(
        t('tips.sections.wgLife.items', {
            returnObjects: true
        }) as Array<Pick<TipItem, 'title' | 'description'>>,
        [<PeopleIcon />, <PeopleIcon />, <HomeIcon />, <CheckIcon />]
    )

    const costTips = withIcons(
        t('tips.sections.costs.items', {
            returnObjects: true
        }) as Array<Pick<TipItem, 'title' | 'description'>>,
        [<MoneyIcon />, <MoneyIcon />, <HomeIcon />, <SecurityIcon />]
    )

    const faqItems = t('tips.sections.faq.items', {
        returnObjects: true
    }) as FaqItem[]

    const quickTips = t('tips.sections.quick.items', {
        returnObjects: true
    }) as string[]

    const sectionLinks = [
        {
            href: '#search-tips',
            label: t('tips.sections.search.title'),
            icon: <SearchIcon />
        },
        {
            href: '#security-tips',
            label: t('tips.sections.security.title'),
            icon: <SecurityIcon />
        },
        {
            href: '#wg-life-tips',
            label: t('tips.sections.wgLife.title'),
            icon: <PeopleIcon />
        },
        {
            href: '#cost-tips',
            label: t('tips.sections.costs.title'),
            icon: <MoneyIcon />
        },
        {
            href: '#faq',
            label: t('tips.sections.faq.title'),
            icon: <InfoIcon />
        }
    ]

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
            {/* Breadcrumb Navigation */}
            <Breadcrumbs
                aria-label="breadcrumb"
                separator={<NavigateNextIcon fontSize="small" />}
                sx={{ mb: 3 }}
            >
                <Link
                    underline="hover"
                    color="inherit"
                    href="/"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                    <HomeIconBreadcrumb fontSize="small" />
                    Startseite
                </Link>
                <Typography color="text.primary">WG-Tipps & Ratgeber</Typography>
            </Breadcrumbs>

            {/* Hero Section */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 3, md: 6 },
                    mb: 6,
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    borderRadius: 3,
                    textAlign: 'center'
                }}
            >
                <Button
                    variant="text"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                        mb: { xs: 3, md: 4 },
                        textTransform: 'none',
                        fontWeight: 600
                    }}
                >
                    {t('common.back')}
                </Button>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {t('tips.heroTitle')}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" paragraph>
                        {t('tips.heroSubtitle')}
                    </Typography>
                    <Typography
                        variant="h2"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '2rem', md: '3rem' },
                            lineHeight: 1.2,
                            color: 'primary.main'
                        }}
                    >
                        WG-Tipps & Ratgeber
                    </Typography>
                    <Typography
                        variant="h5"
                        color="text.secondary"
                        paragraph
                        sx={{
                            fontSize: { xs: '1.1rem', md: '1.3rem' },
                            lineHeight: 1.6,
                            maxWidth: '600px',
                            mx: 'auto'
                        }}
                    >
                        Professionelle Anleitung für Ihre erfolgreiche WG-Suche und ein harmonisches WG-Leben
                    </Typography>
                </Box>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={{ xs: 1.5, md: 2 }}
                    justifyContent="center"
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{
                        '& .MuiButton-root': {
                            width: { xs: '100%', md: 'auto' }
                        }
                    }}
                >
                    {sectionLinks.map((link, index) => (
                        <Button
                            key={link.href}
                            component="a"
                            href={link.href}
                            startIcon={link.icon}
                            variant={index === 0 ? 'contained' : 'outlined'}
                            color="primary"
                            sx={{
                                px: { xs: 2.5, md: 3 },
                                py: { xs: 1.2, md: 1.4 },
                                borderRadius: 999,
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: 'none',
                                bgcolor: index === 0 ? 'primary.main' : 'rgba(255,255,255,0.8)',
                                color: index === 0 ? 'primary.contrastText' : 'primary.main',
                                borderColor: index === 0 ? 'primary.main' : 'primary.main',
                                '&:hover': {
                                    boxShadow: index === 0 ? '0 6px 18px rgba(25, 118, 210, 0.25)' : 'none',
                                    bgcolor: index === 0 ? 'primary.dark' : 'rgba(25,118,210,0.12)'
                                }
                            }}
                        >
                            {link.label}
                        </Button>
                    ))}
                </Stack>
            </Paper>

            {/* Warning Alert */}
            <Alert severity="warning" sx={{ mb: 4 }}>
                <AlertTitle>{t('tips.alertTitle')}</AlertTitle>
                {t('tips.alertDescription')}
            </Alert>

            {/* Search Tips */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SearchIcon color="primary" />
                    {t('tips.sections.search.title')}
                </Typography>
                {/* Quick Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 4 }}>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<SearchIcon />}
                        onClick={() => navigate('/')}
                        sx={{
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '1.1rem'
                        }}
                    >
                        WG finden
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<DownloadIcon />}
                        sx={{
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '1.1rem'
                        }}
                    >
                        Checkliste herunterladen
                    </Button>
                </Box>
            </Box>

            {/* Table of Contents */}
            <Paper elevation={1} sx={{ p: 3, mb: 6, borderRadius: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <ArticleIcon color="primary" />
                    Inhaltsverzeichnis
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Link href="#search-tips" sx={{ textDecoration: 'none' }}>
                            <Chip
                                label="WG-Suche"
                                variant="outlined"
                                icon={<SearchIcon />}
                                sx={{ width: '100%', justifyContent: 'flex-start' }}
                            />
                        </Link>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Link href="#security-tips" sx={{ textDecoration: 'none' }}>
                            <Chip
                                label="Sicherheit"
                                variant="outlined"
                                icon={<SecurityIcon />}
                                sx={{ width: '100%', justifyContent: 'flex-start' }}
                            />
                        </Link>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Link href="#wg-life-tips" sx={{ textDecoration: 'none' }}>
                            <Chip
                                label="WG-Leben"
                                variant="outlined"
                                icon={<PeopleIcon />}
                                sx={{ width: '100%', justifyContent: 'flex-start' }}
                            />
                        </Link>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Link href="#cost-tips" sx={{ textDecoration: 'none' }}>
                            <Chip
                                label="Kosten"
                                variant="outlined"
                                icon={<MoneyIcon />}
                                sx={{ width: '100%', justifyContent: 'flex-start' }}
                            />
                        </Link>
                    </Grid>
                </Grid>
            </Paper>

            {/* Critical Warning Alert */}
            <Alert
                severity="warning"
                sx={{
                    mb: 6,
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                        width: '100%'
                    }
                }}
            >
                <AlertTitle sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                    ⚠️ Wichtiger Sicherheitshinweis
                </AlertTitle>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Seien Sie immer vorsichtig bei WG-Anzeigen. Besichtigen Sie persönlich und zahlen Sie niemals im Voraus ohne Vertrag!
                </Typography>
            </Alert>

            {/* Search Tips */}
            <Box id="search-tips" sx={{ mb: 8 }}>
                <Typography
                    variant="h3"
                    component="h2"
                    gutterBottom
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 4,
                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                        fontWeight: 600,
                        color: 'primary.main'
                    }}
                >
                    <SearchIcon sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }} />
                    Tipps zur WG-Suche
                </Typography>
                <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4, fontSize: '1.1rem' }}>
                    Erfolgreiche Strategien für Ihre WG-Suche
                </Typography>
                <Grid container spacing={4}>
                    {searchTips.map((tip, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                bgcolor: 'primary.light',
                                                color: 'primary.contrastText',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {tip.icon}
                                        </Box>
                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1.3rem',
                                                color: 'text.primary'
                                            }}
                                        >
                                            {tip.title}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'text.secondary',
                                            fontSize: '1.1rem',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        {tip.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Security Tips */}
            <Box id="security-tips" sx={{ mb: 8 }}>
                <Typography
                    variant="h3"
                    component="h2"
                    gutterBottom
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 4,
                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                        fontWeight: 600,
                        color: 'error.main'
                    }}
                >
                    <SecurityIcon sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }} />
                    Sicherheit & Betrugsschutz
                </Typography>
                <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4, fontSize: '1.1rem' }}>
                    Schützen Sie sich vor Betrug und unseriösen Anbietern
                </Typography>
                <Grid container spacing={4}>
                    {securityTips.map((tip, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    border: '2px solid',
                                    borderColor: 'error.light',
                                    boxShadow: '0 4px 20px rgba(244, 67, 54, 0.1)',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 30px rgba(244, 67, 54, 0.2)',
                                        borderColor: 'error.main'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                bgcolor: 'error.light',
                                                color: 'error.contrastText',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {tip.icon}
                                        </Box>
                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1.3rem',
                                                color: 'error.main'
                                            }}
                                        >
                                            {tip.title}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'text.secondary',
                                            fontSize: '1.1rem',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        {tip.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* WG Life Tips */}
            <Box id="wg-life-tips" sx={{ mb: 8 }}>
                <Typography
                    variant="h3"
                    component="h2"
                    gutterBottom
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 4,
                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                        fontWeight: 600,
                        color: 'success.main'
                    }}
                >
                    <PeopleIcon sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }} />
                    Harmonisches WG-Leben
                </Typography>
                <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4, fontSize: '1.1rem' }}>
                    Tipps für ein erfolgreiches Zusammenleben in der WG
                </Typography>
                <Grid container spacing={4}>
                    {wgLifeTips.map((tip, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    boxShadow: '0 4px 20px rgba(76, 175, 80, 0.1)',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 30px rgba(76, 175, 80, 0.15)'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                bgcolor: 'success.light',
                                                color: 'success.contrastText',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {tip.icon}
                                        </Box>
                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1.3rem',
                                                color: 'text.primary'
                                            }}
                                        >
                                            {tip.title}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'text.secondary',
                                            fontSize: '1.1rem',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        {tip.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Cost Tips */}
            <Box id="cost-tips" sx={{ mb: 8 }}>
                <Typography
                    variant="h3"
                    component="h2"
                    gutterBottom
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 4,
                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                        fontWeight: 600,
                        color: 'warning.main'
                    }}
                >
                    <MoneyIcon sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }} />
                    Kosten & Finanzen
                </Typography>
                <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4, fontSize: '1.1rem' }}>
                    Finanzielle Planung für Ihr WG-Leben
                </Typography>
                <Grid container spacing={4}>
                    {costTips.map((tip, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    boxShadow: '0 4px 20px rgba(255, 152, 0, 0.1)',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 30px rgba(255, 152, 0, 0.15)'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                bgcolor: 'warning.light',
                                                color: 'warning.contrastText',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {tip.icon}
                                        </Box>
                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1.3rem',
                                                color: 'text.primary'
                                            }}
                                        >
                                            {tip.title}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'text.secondary',
                                            fontSize: '1.1rem',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        {tip.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FAQ Section */}
            <Box id="faq" sx={{ mb: 8 }}>
                <Typography
                    variant="h3"
                    component="h2"
                    gutterBottom
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 4,
                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                        fontWeight: 600,
                        color: 'info.main'
                    }}
                >
                    <InfoIcon sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }} />
                    Häufige Fragen (FAQ)
                </Typography>
                <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4, fontSize: '1.1rem' }}>
                    Antworten auf die wichtigsten Fragen zur WG-Suche
                </Typography>
                <Paper elevation={1} sx={{ borderRadius: 2 }}>
                    {faqItems.map((item, index) => (
                        <Accordion
                            key={index}
                            sx={{
                                '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
                                '&:before': { display: 'none' }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    py: 2,
                                    '& .MuiAccordionSummary-content': {
                                        margin: '8px 0'
                                    }
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                    {item.question}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '1.1rem',
                                        lineHeight: 1.6
                                    }}
                                >
                                    {item.answer}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Paper>
            </Box>

            {/* Quick Tips */}
            <Box id="quick-tips" sx={{ mb: 6 }}>
                <Typography variant="h4" gutterBottom>
                    {t('tips.sections.quick.title')}
                </Typography>
                <Grid container spacing={2}>
                    {quickTips.map((tip, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Chip
                                label={tip}
                                color="primary"
                                variant="outlined"
                                sx={{ width: '100%', height: 'auto', p: 1 }}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Contact Info */}
            <Box id="contact-info" sx={{ textAlign: 'center', p: 4, bgcolor: 'grey.100', borderRadius: 2 }}>
                <Typography variant="h5" gutterBottom>
                    {t('tips.sections.contact.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                    {t('tips.sections.contact.description')}
                </Typography>
                <Button
                    component="a"
                    href="mailto:support@wg-anzeigen.de"
                    variant="contained"
                    color="primary"
                    aria-label={t('tips.sections.contact.ariaLabel')}
                    sx={{ mt: 2, fontSize: '1.1rem', px: 3, py: 1.5 }}
                >
                    {t('tips.sections.contact.cta')}
                </Button>
            </Box>
            <Box id="quick-checklist" sx={{ mb: 8 }}>
                <Typography
                    variant="h3"
                    component="h2"
                    gutterBottom
                    sx={{
                        mb: 4,
                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                        fontWeight: 600,
                        color: 'primary.main'
                    }}
                >
                    Schnell-Tipps Checkliste
                </Typography>
                <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4, fontSize: '1.1rem' }}>
                    Wichtige Punkte für Ihre WG-Suche im Überblick
                </Typography>
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                    <Grid container spacing={2}>
                        {[
                            "Erstellen Sie ein ansprechendes Profilbild",
                            "Schreiben Sie eine persönliche Bewerbung",
                            "Seien Sie pünktlich bei Besichtigungen",
                            "Stellen Sie Fragen zur WG und den Mitbewohnern",
                            "Bringen Sie alle wichtigen Unterlagen mit",
                            "Seien Sie ehrlich über Ihre Gewohnheiten",
                            "Zeigen Sie Interesse an der WG-Gemeinschaft",
                            "Bleiben Sie höflich auch bei Absagen"
                        ].map((tip, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                                    <CheckIcon color="success" fontSize="small" />
                                    <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                                        {tip}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Box>

            {/* Action Section */}
            <Paper
                elevation={2}
                sx={{
                    textAlign: 'center',
                    p: { xs: 4, md: 6 },
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                }}
            >
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                        mb: 2
                    }}
                >
                    Bereit für Ihre WG-Suche?
                </Typography>
                <Typography
                    variant="h6"
                    paragraph
                    sx={{
                        fontSize: { xs: '1.1rem', md: '1.3rem' },
                        opacity: 0.9,
                        maxWidth: '600px',
                        mx: 'auto',
                        mb: 4
                    }}
                >
                    Starten Sie jetzt Ihre Suche nach der perfekten WG oder erstellen Sie Ihre eigene Anzeige.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<SearchIcon />}
                        onClick={() => navigate('/')}
                        sx={{
                            px: 4,
                            py: 2,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '1.1rem',
                            bgcolor: 'white',
                            color: 'primary.main',
                            '&:hover': {
                                bgcolor: 'grey.100'
                            }
                        }}
                    >
                        WG finden
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<HomeIcon />}
                        onClick={() => navigate('/new')}
                        sx={{
                            px: 4,
                            py: 2,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '1.1rem',
                            borderColor: 'white',
                            color: 'white',
                            '&:hover': {
                                borderColor: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)'
                            }
                        }}
                    >
                        Anzeige erstellen
                    </Button>
                </Box>
            </Paper>
        </Container>
    )
}

export default Tips
